import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { clear, getInsuranceProvider } from '../../actions/insurance';
import { asHtml } from '../../helpers/markdown';
import type { RootState } from '../../reducers';
import { getRetailerId } from '../../selectors/insurance';
import { getItem } from '../../selectors/item';
import InsuranceHeader from './insurance-header';

import type { InsuranceProviderResponse } from '../../@types/Insurance';

import { getMarket } from '../../i18n';
import { Align } from '../../styled-components/components/Align';
import { Box } from '../../styled-components/components/Box';
import Content from '../../styled-components/components/Content';
import { LinkButton } from '../../styled-components/components/LinkButton';
import { Stack, StackSmall, StackTiny } from '../../styled-components/components/Stack';
import { Text } from '../../styled-components/components/Text';
import { Img } from '../../styled-components/components/Utilities';

interface InsuranceProps {
  onClick: () => void;
}

const Insurance = ({ onClick }: InsuranceProps) => {
  const { t } = useTranslation(['item']);
  const dispatch = useDispatch();
  const item = useSelector(getItem);
  const retailerId = useSelector(getRetailerId);
  const provider = useSelector<RootState, InsuranceProviderResponse | null>(
    (s) => s.insurances.provider,
  );
  const actions = bindActionCreators(
    { clearInsurance: clear, getProvider: getInsuranceProvider },
    dispatch,
  );
  const { marketCode } = getMarket();

  const [logo, setLogo] = useState(provider && provider.logo);

  useEffect(() => {
    return () => {
      actions.clearInsurance();
    };
  }, []);

  useEffect(() => {
    if (item?.id && retailerId) {
      actions.getProvider(item.id, retailerId);
    }
  }, [item?.id, retailerId]);

  useEffect(() => {
    if (provider) setLogo(provider.logo);
  }, [provider]);

  if (provider == null) return null;

  return (
    <div className="convert-card-body">
      <StackTiny>
        <Text $weight="medium" $size="xsmallStatic">
          {t('insurance.addition', { ns: 'item' })}
        </Text>
      </StackTiny>
      <StackTiny>
        <Box $relative>
          <Stack>
            <StackSmall>
              <InsuranceHeader />
            </StackSmall>
            <StackSmall>
              <Text $size="xsmallStatic">
                {provider?.description &&
                  (marketCode === 'SE' ? provider?.institute !== 'IF' : true) && (
                    <Content
                      fontSize="current"
                      tone="alt"
                      fullWidth
                      dangerouslySetInnerHTML={{ __html: asHtml(`${provider?.description}`) }}
                    />
                  )}
                {provider.requiresDistance &&
                provider.requiresPersonalNumber &&
                marketCode === 'SE' ? (
                  <LinkButton
                    onClick={onClick}
                    title={t('insurance.chooseYourInsurance', { ns: 'item' })}
                    data-wayke-action="insurance-select"
                    $hoverGhost
                    $block
                  >
                    {t('insurance.chooseYourInsurance', { ns: 'item' })}
                  </LinkButton>
                ) : (
                  <LinkButton
                    onClick={onClick}
                    title={t('insurance.readMore.title', { ns: 'item' })}
                    data-wayke-action="insurance-read-more"
                    $hoverGhost
                    $block
                  >
                    {t('insurance.readMore.text', { ns: 'item' })}
                  </LinkButton>
                )}
              </Text>
            </StackSmall>
          </Stack>
          {logo && (
            <Stack>
              <Align $align="center">
                <Img
                  src={`${logo}?w=80&h=40`}
                  srcSet={`${logo}?w=160&h=80 2x`}
                  alt={provider.institute}
                  style={{ maxWidth: '80px' }}
                />
              </Align>
            </Stack>
          )}
        </Box>
      </StackTiny>
    </div>
  );
};

export default Insurance;
